import { useCallback } from 'react';
import { serialize } from 'cookie';
import { datadogRum } from '@datadog/browser-rum';
import { useApolloClient } from '@apollo/client';
import { useLogOutMutation } from '../../graphql';
import { resetStore } from '../../lib/localStore';
import { clearPersistedSession } from '../../lib/clearPersistedSession';
import { useBroadcastChannel } from './useBroadcast';
import type { SeaberApolloClient } from '../../lib/init-apollo';

export const clearToken = () => {
  document.cookie = serialize('token', '', {
    expires: new Date(1970, 1, 1, 0, 0, 1),
    maxAge: 0,
  });
};

const useLogout = (): (() => Promise<void>) => {
  const client = useApolloClient();
  const [logOutMutation] = useLogOutMutation();

  const logoutHandler = useCallback(async () => {
    try {
      datadogRum.clearUser();
      await logOutMutation();
      await resetStore();
      clearPersistedSession();
      await (client as SeaberApolloClient).logout();

      clearToken();
      window.location.href = '/signin';
    } catch (err) {
      console.error('Error logging out', err);
    }
  }, [client, logOutMutation]);

  const { postMessage } = useBroadcastChannel('logout-channel', (message) => {
    if (message === 'logout') {
      void logoutHandler();
    }
  });

  return () => {
    postMessage('logout');
    return logoutHandler();
  };
};

export default useLogout;
